import { useMemo, useState, type FunctionComponent } from "react";
import { Badge, Collapse, Empty, Input, Segmented, Select, Space, Tag } from "antd";
import dayjs from "dayjs";

import { PriceStatus, type ProductPrice } from "@fitness-app/data-models/entities/Product";

import { useCheckoutVariants } from "~/modules/Products/Product/ProductDetails/hooks/useCheckoutVariants";
import ProductPrices from "~/modules/Products/Product/ProductPrices/ProductPrices";

interface ProductPricesBrowserProps {
  prices: ProductPrice[];
  productSlug: string;
  productId: string;
  productName?: string;
}

type GroupBy = "year" | "paymentType" | "status" | "variant" | "none";
type StatusFilter = "all" | "active" | "inactive" | "hidden" | "visible";
type PaymentTypeFilter = "recurring" | "installments" | "oneTimeWithAccess" | "oneTime";

const GROUPING_STORAGE_KEY = "product-prices-group-by";

const PAGE_SIZE = 25;

const PAYMENT_TYPE_LABELS: Record<PaymentTypeFilter, string> = {
  recurring: "Subskrypcja",
  installments: "Raty",
  oneTimeWithAccess: "Jednorazowa z dostępem czasowym",
  oneTime: "Jednorazowa bezterminowa",
};

const PAYMENT_TYPE_ORDER: PaymentTypeFilter[] = ["recurring", "installments", "oneTimeWithAccess", "oneTime"];

const getPaymentTypeKey = (price: ProductPrice): PaymentTypeFilter => {
  if (price.type === "recurring") {
    return price.installments ? "installments" : "recurring";
  }

  return price.accessPeriod ? "oneTimeWithAccess" : "oneTime";
};

const matchesStatus = (price: ProductPrice, filter: StatusFilter) => {
  switch (filter) {
    case "active":
      return price.active;
    case "inactive":
      return !price.active;
    case "hidden":
      return Boolean(price.hidden);
    case "visible":
      return !price.hidden;
    default:
      return true;
  }
};

const matchesSearch = (price: ProductPrice, search: string) => {
  if (!search) {
    return true;
  }

  const amount = price.unit_amount != null ? String(price.unit_amount / 100) : "";

  return [price.id, price.customLabel, price.customInvoiceName, amount]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(search));
};

const readStoredGrouping = (): GroupBy => {
  const stored = typeof window === "undefined" ? null : window.localStorage.getItem(GROUPING_STORAGE_KEY);

  return stored === "year" ||
    stored === "paymentType" ||
    stored === "status" ||
    stored === "variant" ||
    stored === "none"
    ? stored
    : "year";
};

/**
 * Filtering and grouping around the price table. A campaign-heavy product accumulates a few
 * hundred prices, and rendering them as one flat table is what made opening the product drag —
 * grouped panels mount only the group that is opened. "Bez grupowania" keeps the old flat view,
 * paginated.
 */
const ProductPricesBrowser: FunctionComponent<ProductPricesBrowserProps> = ({
  prices,
  productSlug,
  productId,
  productName,
}) => {
  const { data: checkoutVariants } = useCheckoutVariants(productId);
  const [groupBy, setGroupBy] = useState<GroupBy>(readStoredGrouping);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<PaymentTypeFilter[]>([]);

  const handleGroupByChange = (value: GroupBy) => {
    setGroupBy(value);
    window.localStorage.setItem(GROUPING_STORAGE_KEY, value);
  };

  const visiblePrices = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return prices
      .filter((price) => price.status !== PriceStatus.Archived)
      .filter((price) => matchesStatus(price, statusFilter))
      .filter((price) => !paymentTypeFilter.length || paymentTypeFilter.includes(getPaymentTypeKey(price)))
      .filter((price) => matchesSearch(price, normalizedSearch));
  }, [prices, search, statusFilter, paymentTypeFilter]);

  const groups = useMemo((): { key: string; label: string; prices: ProductPrice[] }[] => {
    if (groupBy === "none") {
      return [];
    }

    if (groupBy === "year") {
      // `createdAt` is a unix timestamp on the gateway price; prices predating it group
      // under "Bez daty" rather than silently landing in 1970.
      const byYear = new Map<string, ProductPrice[]>();

      for (const price of visiblePrices) {
        const year = price.createdAt ? dayjs.unix(price.createdAt).format("YYYY") : "—";
        const bucket = byYear.get(year);
        if (bucket) {
          bucket.push(price);
        } else {
          byYear.set(year, [price]);
        }
      }

      return Array.from(byYear.entries())
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([year, yearPrices]) => ({ key: year, label: year === "—" ? "Bez daty" : year, prices: yearPrices }));
    }

    if (groupBy === "variant") {
      const byVariant = (checkoutVariants ?? []).map((variant) => ({
        key: variant.id,
        label: variant.slug,
        prices: visiblePrices.filter((price) => variant.prices.includes(price.id)),
      }));

      const assignedPriceIds = new Set((checkoutVariants ?? []).flatMap((variant) => variant.prices));
      const unassigned = visiblePrices.filter((price) => !assignedPriceIds.has(price.id));

      return [...byVariant, { key: "__none__", label: "Poza wariantami", prices: unassigned }].filter(
        (group) => group.prices.length,
      );
    }

    if (groupBy === "status") {
      const buckets: { key: StatusFilter; label: string; match: (price: ProductPrice) => boolean }[] = [
        { key: "active", label: "Aktywne i widoczne", match: (price) => price.active && !price.hidden },
        { key: "hidden", label: "Aktywne, ukryte", match: (price) => price.active && Boolean(price.hidden) },
        { key: "inactive", label: "Nieaktywne", match: (price) => !price.active },
      ];

      return buckets
        .map((bucket) => ({ key: bucket.key, label: bucket.label, prices: visiblePrices.filter(bucket.match) }))
        .filter((group) => group.prices.length);
    }

    return PAYMENT_TYPE_ORDER.map((key) => ({
      key,
      label: PAYMENT_TYPE_LABELS[key],
      prices: visiblePrices.filter((price) => getPaymentTypeKey(price) === key),
    })).filter((group) => group.prices.length);
  }, [groupBy, visiblePrices, checkoutVariants]);

  const renderTable = (groupPrices: ProductPrice[]) => (
    <ProductPrices
      extended
      productSlug={productSlug}
      prices={groupPrices}
      productId={productId}
      productName={productName}
      // `defaultPageSize`, not `pageSize`: antd merges a passed `pageSize` over its own state on
      // every render, so the "na stronę" selector would visibly do nothing. The table hides the
      // footer by itself when everything fits on one page.
      pagination={{
        defaultPageSize: PAGE_SIZE,
        showSizeChanger: true,
        hideOnSinglePage: true,
        size: "small",
        showTotal: (total) => `${total} cen`,
      }}
    />
  );

  return (
    <div>
      <Space wrap size="middle" className="w-full justify-between">
        <Space wrap size="small">
          <Input.Search
            allowClear
            placeholder="Szukaj po ID, etykiecie lub kwocie"
            style={{ width: 280 }}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select<PaymentTypeFilter[]>
            mode="multiple"
            allowClear
            style={{ minWidth: 240 }}
            placeholder="Typ płatności"
            value={paymentTypeFilter}
            onChange={setPaymentTypeFilter}
            options={PAYMENT_TYPE_ORDER.map((key) => ({ value: key, label: PAYMENT_TYPE_LABELS[key] }))}
          />
          <Select<StatusFilter>
            style={{ width: 180 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: "all", label: "Wszystkie statusy" },
              { value: "active", label: "Aktywne" },
              { value: "inactive", label: "Nieaktywne" },
              { value: "visible", label: "Widoczne" },
              { value: "hidden", label: "Ukryte" },
            ]}
          />
        </Space>
        <Segmented<GroupBy>
          value={groupBy}
          onChange={handleGroupByChange}
          options={[
            { value: "year", label: "Rok utworzenia" },
            { value: "paymentType", label: "Typ płatności" },
            { value: "status", label: "Status" },
            { value: "variant", label: "Wariant koszyka" },
            { value: "none", label: "Bez grupowania" },
          ]}
        />
      </Space>

      <div className="mt-2">
        <Tag>
          {visiblePrices.length} / {prices.filter((price) => price.status !== PriceStatus.Archived).length} cen
        </Tag>
      </div>

      {!visiblePrices.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak cen spełniających filtry" className="py-10" />
      ) : groupBy === "none" ? (
        renderTable(visiblePrices)
      ) : (
        <Collapse
          className="mt-4"
          items={groups.map((group) => ({
            key: group.key,
            label: (
              <Space>
                <span>{group.label}</span>
                <Badge count={group.prices.length} showZero color="#6366f1" overflowCount={999} />
              </Space>
            ),
            children: renderTable(group.prices),
          }))}
        />
      )}
    </div>
  );
};

export default ProductPricesBrowser;
